import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CTAButton from '../../components/base/CTAButton';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function CinewallPrijs() {
  useSEO({
    title: 'Cinewall prijs | Wat kost een cinewall op maat?',
    description: 'Bekijk prijsopbouw en ontvang een vrijblijvende offerte.',
    keywords: 'cinewall prijs, cinewall kosten, wat kost een cinewall, cinewall op maat prijs',
    canonical: `${import.meta.env.VITE_SITE_URL}/cinewall-prijs`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Cinewall prijs | Wat kost een cinewall op maat?',
      description: 'Transparante prijsopbouw voor cinewalls op maat. Vanaf €2.500 tot €7.500+ afhankelijk van afmetingen, materialen en opties.',
      url: `${import.meta.env.VITE_SITE_URL}/cinewall-prijs`,
      mainEntity: {
        '@type': 'Product',
        name: 'Cinewall op maat',
        description: 'Maatwerk cinewall met tv, haard en LED-verlichting',
        offers: {
          '@type': 'AggregateOffer',
          priceCurrency: 'EUR',
          lowPrice: '2500',
          highPrice: '7500',
          offerCount: '3',
        },
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const priceFactors = [
    { icon: 'ri-ruler-line', title: 'Afmetingen', description: 'Grootte en complexiteit van de wand' },
    { icon: 'ri-paint-brush-line', title: 'Materialen', description: 'Keuze uit premium afwerkingen' },
    { icon: 'ri-fire-line', title: 'Haardtype', description: 'Elektrisch of waterdamp haard' },
    { icon: 'ri-lightbulb-line', title: 'LED-verlichting', description: 'Sfeerverlichting en nissen' },
    { icon: 'ri-archive-line', title: 'Opbergruimte', description: 'Kasten en laden op maat' },
    { icon: 'ri-tools-line', title: 'Montage', description: 'Professionele plaatsing en afwerking' }
  ];

  const priceRanges = [
    {
      title: 'Basis cinewall',
      price: 'Vanaf €2.500',
      features: [
        'Maatwerk wandmeubel',
        'TV-integratie',
        'Basis LED-verlichting',
        'Strakke afwerking',
        'Professionele montage'
      ],
      note: 'Zonder haard'
    },
    {
      title: 'Luxe uitvoering',
      price: '€3.500 – €6.000',
      features: [
        'Premium materialen',
        'Uitgebreide opbergruimte',
        'Geavanceerde LED-verlichting',
        'Luxe afwerking',
        'Volledige integratie'
      ],
      note: 'Zonder haard',
      highlight: true
    },
    {
      title: 'Complete cinewall',
      price: '€4.000 – €7.500+',
      features: [
        'Inclusief haard',
        'Premium materialen',
        'Volledige maatwerk',
        'Luxe LED-verlichting',
        'Technisch perfect'
      ],
      note: 'Met haard en alle opties'
    }
  ];

  const included = [
    'Persoonlijk intakeadvies',
    'Technisch ontwerp en tekeningen',
    'Productie op maat in eigen werkplaats',
    'Professionele montage door vakmanschap',
    'Technisch veilige opbouw',
    'Garantie op materiaal en montage'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif text-gray-900 mb-6">
            Wat kost een cinewall op maat?
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Elke cinewall is maatwerk en daarom uniek in prijs. De investering hangt af van uw wensen, 
            afmetingen en materiaalkeuze. Hieronder vindt u een transparante prijsopbouw.
          </p>
        </div>
      </section>

      {/* SEO Content Section - NEW */}
      <section className="pb-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-6">
            Wat bepaalt de prijs van een cinewall?
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            De <strong>prijs van een cinewall</strong> wordt bepaald door afmetingen, afwerking, 
            opbergruimte en het type haard.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Omdat wij werken met <strong>maatwerk</strong>, ontvangt u via onze{' '}
            <Link to="/cinewall-configurator" className="text-[#C7A86A] hover:underline font-semibold">
              configurator
            </Link>{' '}
            altijd een realistische indicatie op basis van uw keuzes.
          </p>
        </div>
      </section>

      {/* Configurator CTA */}
      <section className="pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-[#0F0F0F] to-[#1A1A1A] rounded-2xl p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-64 h-64 bg-[#C7A86A] rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#C7A86A] rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 flex items-center justify-center bg-[#C7A86A]/10 rounded-full mx-auto mb-6">
                <i className="ri-settings-3-line text-3xl text-[#C7A86A]"></i>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
                Bereken uw cinewall indicatie in 2 minuten
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                Beantwoord enkele vragen en ontvang direct een indicatie + persoonlijk advies.
              </p>
              
              <Link
                to="/cinewall-configurator"
                className="inline-flex items-center gap-3 bg-[#C7A86A] text-[#0F0F0F] px-8 py-4 rounded-md font-semibold text-base hover:bg-[#B89858] transition-all duration-300 shadow-lg shadow-[#C7A86A]/20 whitespace-nowrap cursor-pointer"
              >
                Start configurator
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Price Factors */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">
              Factoren die de prijs bepalen
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Geen vaste pakketten, maar maatwerk dat perfect aansluit bij uw wensen en budget
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {priceFactors.map((factor, index) => (
              <div key={index} className="bg-white p-8 rounded-lg">
                <div className="w-14 h-14 flex items-center justify-center bg-stone-100 rounded-lg mb-6">
                  <i className={`${factor.icon} text-2xl text-stone-800`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {factor.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {factor.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price Ranges */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">
              Richtprijzen
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Indicatieve prijzen voor verschillende uitvoeringen
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {priceRanges.map((range, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-lg p-8 ${
                  range.highlight 
                    ? 'ring-2 ring-stone-800 shadow-xl' 
                    : 'border border-stone-200'
                }`}
              >
                {range.highlight && (
                  <div className="inline-block bg-stone-800 text-white text-sm px-4 py-1 rounded-full mb-4">
                    Populair
                  </div>
                )}
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  {range.title}
                </h3>
                <div className="text-3xl font-serif text-stone-800 mb-6">
                  {range.price}
                </div>
                <ul className="space-y-3 mb-6">
                  {range.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-stone-800 text-xl mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-500 italic border-t border-stone-200 pt-4">
                  {range.note}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-6 bg-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">
              Wat is altijd inbegrepen
            </h2>
            <p className="text-lg text-gray-600">
              Ongeacht uw keuze, deze service is standaard
            </p>
          </div>

          <div className="bg-white rounded-lg p-10">
            <div className="grid md:grid-cols-2 gap-6">
              {included.map((item, index) => (
                <div key={index} className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-stone-800 text-2xl mr-4 flex-shrink-0"></i>
                  <span className="text-gray-800 text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Invest */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-serif text-gray-900 mb-8 text-center">
            Waarom investeren in kwaliteit loont
          </h2>
          
          <div className="space-y-8">
            <div className="bg-stone-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Technisch veilig en duurzaam
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Een goedkope cinewall kan leiden tot technische problemen: oververhitting van de tv, 
                onveilige haardplaatsing of constructiefouten. Wij bouwen volgens de juiste normen 
                en zorgen voor een veilige, duurzame oplossing.
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Tijdloos design en perfecte afwerking
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Een cinewall is een investering voor jaren. Door te kiezen voor kwaliteit en maatwerk 
                creëert u een tijdloos interieur dat perfect aansluit bij uw woning en levensstijl.
              </p>
            </div>

            <div className="bg-stone-50 p-8 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Persoonlijke begeleiding van A tot Z
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Van eerste adviesgesprek tot montage: u heeft één aanspreekpunt. 
                Wij denken mee, adviseren eerlijk en leveren wat we beloven.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-stone-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif mb-6">
            Wilt u weten wat een cinewall kost in uw woning?
          </h2>
          <p className="text-xl text-stone-200 mb-10 leading-relaxed">
            Vraag een vrijblijvende offerte aan en ontvang binnen 24 uur een realistische prijsindicatie
          </p>
          <Link to="/cinewall-offerte">
            <CTAButton variant="light">
              Vraag gratis offerte aan
            </CTAButton>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
